<script setup lang="ts">
import { copyTextToClipboard, DialogModal } from '@metapic-apps/shared'
import { computed, ref, toValue, watch } from 'vue'
import { useRoute } from 'vue-router'

import { useDeleteCampaignRequest, useEditCampaignRequest, useGetCampaignRequest, useSetCampaignActivityRequest } from '@/api'
import LinkIcon from '@/assets/icons/link.svg?component'
import PauseIcon from '@/assets/icons/pause.svg?component'
import PlayIcon from '@/assets/icons/play.svg?component'
import CampaignForm from '@/components/CampaignForm/CampaignForm.vue'
import HeaderComponent from '@/components/Navigation/HeaderComponent.vue'
import { config, notification } from '@/lib/helpers'
import { defineCampaignEndReason } from '@/lib/helpers/campaign.helpers'
import { useBackToPreviousPage } from '@/lib/hooks/back-to-previous-page.hooks'
import type { CampaignFormType } from '@/lib/types/campaign.types'
import { CampaignStatusEnum } from '@/lib/types/campaigns.types'
import { useUserStore } from '@/stores/user'
import { BoxComponent, ButtonComponent, FlexComponent, LoadingComponent, TypographyComponent } from '@/ui-library'

const route = useRoute()

const formRef = ref(null)
const handleBack = useBackToPreviousPage({ name: 'campaigns' })
const userStore = useUserStore()
const campaignId = Number(route.params.id as unknown as string)
const { data: campaignData, isLoading: isCampaignLoading } = useGetCampaignRequest(campaignId)
const { editCampaign, isLoading, error } = useEditCampaignRequest(campaignId)
const { isLoading: isStatusLoading, setStatus } = useSetCampaignActivityRequest(campaignId)
const { deleteCampaign: deleteCampaignRequest, isLoading: isDeletingLoading, error: isDeleteError } = useDeleteCampaignRequest()
const isPauseBtnShown = computed(
  () =>
    campaignData.value?.extra?.status &&
    [CampaignStatusEnum.Active, CampaignStatusEnum.Paused, CampaignStatusEnum.Scheduled].includes(campaignData.value?.extra?.status),
)
const endReason = computed((): string => {
  const campaign = toValue(campaignData.value)
  if (campaign) {
    return defineCampaignEndReason(campaign)
  }
  return ''
})
const isCampaignActive = ref(false)
const isDeletePopupOpen = ref(false)

watch(
  () => campaignData.value?.extra?.status,
  () => {
    isCampaignActive.value =
      campaignData.value?.extra?.status === CampaignStatusEnum.Active || campaignData.value?.extra?.status === CampaignStatusEnum.Scheduled
  },
)

const onCampaignEdit = async (data: CampaignFormType) => {
  await editCampaign(data)
  if (!error.value) {
    await handleBack()
    notification.success('The campaign was saved successfully')
  }
}

const setCampaignActiveStatus = async () => {
  const shouldPause = isCampaignActive.value
  await setStatus(shouldPause)
  isCampaignActive.value = !isCampaignActive.value
}

const deleteCampaign = async () => {
  await deleteCampaignRequest(campaignId)
  if (!isDeleteError.value) {
    await handleBack()
    notification.success('The campaign was deleted successfully')
  }
}
</script>

<template>
  <HeaderComponent>
    <FlexComponent v-if="campaignData" :gap="16">
      <ButtonComponent
        v-if="isPauseBtnShown && !endReason"
        variant="transparent"
        :is-loading="isStatusLoading"
        :is-disabled="isStatusLoading"
        @click="setCampaignActiveStatus"
      >
        <template #iconLeft>
          <el-icon :size="24">
            <PauseIcon v-if="isCampaignActive" />
            <PlayIcon v-else />
          </el-icon>
        </template>
        {{ isCampaignActive ? 'Pause' : 'Un-pause' }} campaign
      </ButtonComponent>
      <el-tooltip v-if="endReason" placement="bottom" effect="light">
        <ButtonComponent is-disabled variant="transparent">
          <template #iconLeft>
            <el-icon :size="24">
              <PlayIcon />
            </el-icon>
          </template>
          Un-pause campaign
        </ButtonComponent>
        <template #content>
          <BoxComponent w="250px">
            <TypographyComponent variant="body-medium">
              {{ endReason }}
            </TypographyComponent>
          </BoxComponent>
        </template>
      </el-tooltip>
      <el-tooltip v-if="!endReason" placement="bottom" effect="light" trigger="click" :auto-close="1000">
        <ButtonComponent
          variant="secondary"
          @click="
            () => {
              copyTextToClipboard(`${config.creatorAppUrl}/campaign-invitation?join-campaign=${campaignData?.extra?.token}`)
            }
          "
        >
          <template #iconLeft>
            <el-icon :size="24">
              <LinkIcon />
            </el-icon>
          </template>
          Copy invite link
        </ButtonComponent>
        <template #content> Copied! </template>
      </el-tooltip>
    </FlexComponent>
  </HeaderComponent>
  <LoadingComponent v-if="isCampaignLoading || userStore.isLoading" :size="64" :is-full-size="true" />
  <template v-if="userStore.user">
    <CampaignForm
      v-if="campaignData"
      ref="formRef"
      :is-loading="isLoading || isDeletingLoading"
      :campaign="campaignData"
      :is-edit-mode="true"
      :default-advertiser-group-id="userStore.user.storeGroupId"
      :end-reason="endReason"
      @handle-submit="onCampaignEdit"
      @handle-delete="() => (isDeletePopupOpen = true)"
    />
  </template>
  <DialogModal v-model="isDeletePopupOpen" @before-close="() => (isDeletePopupOpen = false)">
    <TypographyComponent variant="headline-medium">Delete campaign</TypographyComponent>
    <BoxComponent :mt="16" :mb="24">
      <TypographyComponent variant="body-large">
        Are you sure you want to delete this campaign? Deleting this campaign will remove access for all applied creators.
      </TypographyComponent>
    </BoxComponent>
    <FlexComponent :gap="8" justify="flex-end">
      <ButtonComponent variant="text" :is-disabled="isDeletingLoading" @click="() => (isDeletePopupOpen = false)">Cancel</ButtonComponent>
      <ButtonComponent variant="danger" :is-loading="isDeletingLoading" @click="deleteCampaign"> Delete campaign </ButtonComponent>
    </FlexComponent>
  </DialogModal>
</template>
